import { DOCUMENT } from '@angular/common';
import { inject, Injectable, signal } from '@angular/core';
import { LocalStorageService } from './local-storage.service';

export type GridDensity = 'comfortable' | 'standard' | 'compact';

export const GRID_DENSITIES: readonly GridDensity[] = ['comfortable', 'standard', 'compact'] as const;

/**
 * Grid row density.
 *
 * Applied as ONE dataset write on <html>. The density tokens live at :root, so
 * that single attribute change costs the browser one style recalc and every
 * grid on the page re-lays out. Nothing subscribes: no signal read in the 27
 * table templates, no component re-render, no visibleColumns()/cellInputs()
 * graph re-running. The `density` signal here exists only so the control that
 * sets it can show which level is active.
 *
 * Persisted in localStorage under 'ds-density', mirroring ThemeService's
 * 'ds-theme'. It is deliberately not a server preference: there is no per-user
 * preference API - `Preferences/` is a global admin key/value store - and
 * density is a per-person, per-display choice, not an org setting.
 */
@Injectable({ providedIn: 'root' })
export class DensityService {
  private readonly document = inject(DOCUMENT);
  private readonly localStorage = inject(LocalStorageService);
  private static readonly storageKey = 'ds-density';
  private static readonly fallback: GridDensity = 'standard';

  readonly density = signal<GridDensity>(DensityService.fallback);

  /** Applies the stored density, or the default when there is none. */
  restore(): void {
    this.apply(this.stored() ?? DensityService.fallback, false);
  }

  set(density: GridDensity): void {
    this.apply(density, true);
  }

  private apply(density: GridDensity, persist: boolean): void {
    this.density.set(density);

    // Standard is the value :root already carries, so it is expressed by the
    // ABSENCE of the attribute rather than by data-density="standard". That
    // keeps the default path free of a selector that only ever restates the
    // defaults, and means a failure to restore lands on standard rather than
    // on nothing.
    if (density === DensityService.fallback) {
      delete this.document.documentElement.dataset['density'];
    } else {
      this.document.documentElement.dataset['density'] = density;
    }

    if (persist) {
      this.localStorage.encryptAndStore(DensityService.storageKey, density);
    }
  }

  private stored(): GridDensity | null {
    try {
      const raw = this.localStorage.retrieveAndDecrypt(DensityService.storageKey) as GridDensity | null;
      return raw && (GRID_DENSITIES as readonly string[]).includes(raw) ? raw : null;
    } catch {
      // A corrupt or undecryptable value is not worth failing boot over.
      return null;
    }
  }
}
